import {compact} from 'lodash';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';

import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {buildTeammateDisplayName} from '../../../../../helpers/modelHelpers';
import {LensModel} from '../../../../../models/api/lensModel';
import {AppState} from '../../../../../store/appState';
import {makeGetInboxesById} from '../../../../../store/data/inboxes/inboxesSelectors';
import {getTags} from '../../../../../store/data/tags/tagsSelectors';
import {getTeammates} from '../../../../../store/data/teammates/teammatesSelectors';
import {getUserTeams} from '../../../../../store/data/teams/teamsSelectors';
import {LensesWorkspaceIngredientsTooltip} from './lensesWorkspaceIngredientsTooltip';

/*
 * Props.
 */

interface LensesWorkspaceIngredientsTooltipContainerProps {
  lens: LensModel;
}

/*
 * Selectors.
 */

function getLensName(_state: AppState, props: LensesWorkspaceIngredientsTooltipContainerProps) {
  return props.lens.name;
}

function getLensInboxIds(_state: AppState, props: LensesWorkspaceIngredientsTooltipContainerProps) {
  return props.lens.inboxIds;
}

function getLensTagIds(_state: AppState, props: LensesWorkspaceIngredientsTooltipContainerProps) {
  return props.lens.tagIds;
}

function getLensAssigneeIds(_state: AppState, props: LensesWorkspaceIngredientsTooltipContainerProps) {
  return props.lens.assigneeIds;
}

const getLensInboxesById = makeGetInboxesById(getLensInboxIds);

// Since we don't get the Team namespace from the BE, we get it using the first inbox as all inboxes in a lens should have the same namespace
const getLensInboxesNamespace = createSelector(
  [getLensInboxesById],
  (lensInboxes) => lensInboxes[0].namespace,
);

const getLensTeamName = createSelector(
  [getUserTeams, getLensInboxesNamespace],
  (teams, lensInboxesNamespace) => teams.find((t) => t.namespace === lensInboxesNamespace)?.name,
);

const getLensTeamInboxNames = createSelector([getLensInboxesById], (inboxes) =>
  inboxes.map((inbox) => inbox.name),
);

const getLensTagNames = createSelector([getLensTagIds, getTags], (tagIds, tags) =>
  compact(tagIds.map((tagId) => tags[tagId]?.name)),
);

const getUsersById = createSelector([getLensAssigneeIds, getTeammates], (assigneedIds, teammates) =>
  compact(
    assigneedIds.map((assigneedId) => {
      const teammate = teammates[assigneedId];
      if (!teammate) {
        return null;
      }
      return buildTeammateDisplayName(teammate);
    }),
  ),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<LensesWorkspaceIngredientsTooltipContainerProps>()(() => ({
  lensName: getLensName,
  teamName: getLensTeamName,
  teamInboxes: getLensTeamInboxNames,
  teamTags: getLensTagNames,
  assignees: getUsersById,
}));

function mapDispatchToProps(dispatch: Dispatch, _props: LensesWorkspaceIngredientsTooltipContainerProps) {
  return {};
}

export const LensesWorkspaceIngredientsTooltipContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(LensesWorkspaceIngredientsTooltip);
